import { useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../stores/auth';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Alert } from '../../components/ui/Alert';
import { ApiError } from '../../lib/api';
import { ThemeToggle } from '../../components/ui/ThemeToggle';
import { Icon } from '../../components/ui/Icon';
import '../auth/auth.css';

export function VerifyPage() {
  const { verifyEmail, resendVerification } = useAuth();
  const location = useLocation();
  const initialEmail = (location.state as { email?: string } | null)?.email ?? '';
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      await verifyEmail(email, code);
      setSuccess('Email verified. You can now sign in.');
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Could not verify email. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend() {
    setError(null);
    try {
      await resendVerification(email);
      setSuccess('A new code has been sent.');
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Could not resend code. Please try again.');
      }
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-topbar">
        <Link className="auth-brand" to="/">
          <span className="auth-logo-mark" aria-hidden="true"><Icon name="zap" size={16} /></span>
          <span>Sarnia Builder</span>
        </Link>
        <ThemeToggle />
      </div>
      <div className="auth-main">
        <div className="auth-form-panel">
          <div className="auth-form-card">
            <h1>Verify your email</h1>
            <form onSubmit={handleSubmit}>
              {error ? <Alert variant="error">{error}</Alert> : null}
              {success ? <Alert variant="success">{success}</Alert> : null}
              <Input
                label="Email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <Input
                label="Verification code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                maxLength={6}
              />
              <Button type="submit" loading={submitting} style={{ width: '100%' }}>
                Verify
              </Button>
              <p className="auth-form-footer">
                Didn't receive a code?{' '}
                <button type="button" onClick={handleResend} style={{ background: 'none', border: 'none', color: 'var(--primary)', textDecoration: 'underline' }}>
                  Resend
                </button>
              </p>
              <p className="auth-form-footer">
                <Link to="/login">Back to sign in</Link>
              </p>
            </form>
          </div>
        </div>
        <aside className="auth-side">
          <div className="auth-side__content">
            <h2 className="auth-side__tagline">Small island. Big signal.</h2>
            <ul className="auth-side__list">
              <li className="auth-side__item"><Icon name="check" size={20} /> Visual editing, no code guesswork</li>
              <li className="auth-side__item"><Icon name="check" size={20} /> Designer templates made in Guernsey</li>
              <li className="auth-side__item"><Icon name="check" size={20} /> Free previews and one-click export</li>
            </ul>
          </div>
          <div className="auth-side__preview">
            <img src="/template-previews/design-12.jpg" alt="Sarnia Builder template preview" loading="lazy" />
          </div>
        </aside>
      </div>
    </div>
  );
}
